import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Box,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { BillNameFormat } from './BillNameFormat';
import {
  BulkDownloadConfig,
  BulkDownloadResponse,
} from '../../services/claimsService';
import { MDButton } from '../MDButton';
import { MDTypography } from '../MDTypography';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiPaper-root': {
      width: 'auto',
    },
  },
  dialogContent: {
    minWidth: 600,
    maxWidth: 800,
  },
  section: {
    marginBottom: theme.spacing(1),
  },
  datePickerContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  checkboxContainer: {
    marginTop: theme.spacing(1),
  },
  resultContainer: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export interface BulkDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  onDownload: (config: BulkDownloadConfig) => Promise<BulkDownloadResponse>;
}

export const BulkDownloadDialog: React.FC<BulkDownloadDialogProps> = ({
  open,
  onClose,
  onDownload,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkDownloadResponse | null>(null);

  // Form state
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());
  const [downloadAll, setDownloadAll] = useState(false);
  const [downloadOnlyPending, setDownloadOnlyPending] = useState(true);
  const [filenameFormat, setFilenameFormat] = useState('{caseId}_{billId}');
  const [groupBy, setGroupBy] = useState<
    'none' | 'day' | 'caseId' | 'policyNumber' | 'billType'
  >('none');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const today = new Date();
      setFromDate(today);
      setToDate(today);
      setDownloadAll(false);
      setDownloadOnlyPending(true);
      setFilenameFormat('{caseId}_{billId}');
      setGroupBy('none');
      setError(null);
      setResult(null);
    }
  }, [open]);

  // Format date to YYYY-MM-DD for input
  const formatDateForInput = (date: Date | undefined): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const config: BulkDownloadConfig = {
        timeRange: {
          from: fromDate,
          to: toDate,
        },
        downloadAll,
        downloadOnlyPending,
        filenameFormat: filenameFormat || '{caseId}_{billId}',
        groupBy,
      };

      const response = await onDownload(config);
      setResult(response);
    } catch (err: any) {
      setError(err.message || 'Failed to prepare bulk download');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = () => {
    if (result?.downloadUrl) {
      window.open(result.downloadUrl, '_blank');
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid = fromDate && toDate && filenameFormat;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={classes.dialogRoot}
    >
      <DialogTitle>Bulk Download Bills Configuration</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* Time Range Section */}
        <Box className={classes.section}>
          <MDTypography variant="h6" gutterBottom>
            Time Range
          </MDTypography>
          <Box className={classes.datePickerContainer}>
            <TextField
              id="from-date"
              label="From"
              type="date"
              variant="outlined"
              size="small"
              value={formatDateForInput(fromDate)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFromDate(new Date(e.target.value))
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: formatDateForInput(toDate) || undefined,
              }}
              required
            />
            <TextField
              id="until-date"
              label="Until"
              type="date"
              variant="outlined"
              size="small"
              value={formatDateForInput(toDate)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setToDate(new Date(e.target.value))
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: formatDateForInput(fromDate) || undefined,
              }}
              required
            />
          </Box>
        </Box>

        {/* Download Options Section */}
        <Box className={classes.section}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <MDTypography variant="h6">Download Options</MDTypography>
            </FormLabel>
            <RadioGroup
              value={downloadAll ? 'all' : 'pending'}
              onChange={(e) => {
                const value = e.target.value;
                setDownloadAll(value === 'all');
                setDownloadOnlyPending(value === 'pending');
              }}
            >
              <FormControlLabel
                value="all"
                control={<Radio style={{ color: 'black' }} />}
                label="Download All Bills"
              />
              <FormControlLabel
                value="pending"
                control={<Radio style={{ color: 'black' }} />}
                label="Download Only Pending Bills (Default)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Bill Name Format Section */}
        <Box className={classes.section}>
          <BillNameFormat value={filenameFormat} onChange={setFilenameFormat} />
        </Box>

        {/* Grouping Section */}
        <Box className={classes.section}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <MDTypography variant="h6">Group By</MDTypography>
            </FormLabel>
            <RadioGroup
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as any)}
            >
              <FormControlLabel
                value="none"
                control={<Radio style={{ color: 'black' }} />}
                label="None - All files in one folder"
              />
              <FormControlLabel
                value="day"
                control={<Radio style={{ color: 'black' }} />}
                label="Day - Group by date of bill received"
              />
              <FormControlLabel
                value="caseId"
                control={<Radio style={{ color: 'black' }} />}
                label="Case ID - Group by case"
              />
              <FormControlLabel
                value="policyNumber"
                control={<Radio style={{ color: 'black' }} />}
                label="Policy Number - Group by policy"
              />
              <FormControlLabel
                value="billType"
                control={<Radio style={{ color: 'black' }} />}
                label="Bill Type - Group by Professional/Institutional"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {error}
          </Alert>
        )}

        {/* Result Display */}
        {result && (
          <Box className={classes.resultContainer}>
            <MDTypography variant="h6" color="primary" gutterBottom>
              Download Ready!
            </MDTypography>
            <MDTypography variant="body2">
              <strong>File:</strong> {result.fileName}
            </MDTypography>
            <MDTypography variant="body2">
              <strong>Files Count:</strong> {result.fileCount}
            </MDTypography>
            <MDTypography variant="body2">
              <strong>Total Size:</strong> {result.totalSize}
            </MDTypography>
            <MDTypography variant="body2">
              <strong>Expires At:</strong>{' '}
              {new Date(result.expiresAt).toLocaleString()}
            </MDTypography>
            <Box mt={2}>
              <MDButton
                variant="gradient"
                color="success"
                onClick={handleDownloadFile}
                fullWidth
              >
                Download ZIP File
              </MDButton>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 24px',
        }}
      >
        {!result && (
          <MDButton
            onClick={handleDownload}
            color="info"
            variant="gradient"
            disabled={loading || !isFormValid}
          >
            {loading ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress size={20} color="inherit" />
                Preparing Download...
              </Box>
            ) : (
              'Prepare Download'
            )}
          </MDButton>
        )}
        <MDButton
          onClick={handleClose}
          disabled={loading}
          color="white"
          variant="outlined"
          style={{ color: 'black', borderColor: 'black' }}
        >
          {result ? 'Close' : 'Cancel'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
